import * as React from 'react'
import { useCallback, useState } from 'react'
import { Alert, Button, Form, Input, Select } from 'antd'
import { MailOutlined, SafetyOutlined } from '@ant-design/icons'
import { useCookies } from 'react-cookie'
import { useNavigate, Link as RouterLink } from 'react-router-dom'
import { AuthLayout } from '../../components/AuthLayout'
import { requestVerificationCode, verifyCode, AdminRole } from '../../services/admin-auth-service'

const ROLE_OPTIONS = [
  { label: 'Admin User', value: 'ROLE_ADMIN' },
  { label: 'Organization', value: 'ROLE_ORGANIZATION' },
  { label: 'Doctor', value: 'ROLE_DOCTOR' },
]

const Login = () => {
  const navigate = useNavigate()
  const [, setCookie] = useCookies(['loginToken'])

  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<AdminRole>('ROLE_ADMIN')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const handleRequestCode = useCallback(async (values: { email: string; role: AdminRole }) => {
    setLoading(true)
    setError('')
    try {
      await requestVerificationCode(values.email, values.role)
      setEmail(values.email)
      setRole(values.role)
      setStep('code')
      setSuccess(`Verification code sent to ${values.email}`)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleVerifyCode = useCallback(async (values: { code: string }) => {
    setLoading(true)
    setError('')
    try {
      const accessToken = await verifyCode(email, role, values.code)
      setCookie('loginToken', accessToken, { path: '/' })
      navigate('/')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [email, role, setCookie, navigate])

  return (
    <AuthLayout
      headline={step === 'email' ? 'Sign in to Engage' : 'Enter your code'}
      subtext={
        step === 'email'
          ? 'A one-time verification code will be sent to your email.'
          : `We sent a code to ${email}. Check your inbox.`
      }
      footer={
        step === 'email' ? (
          <span>
            New here? <RouterLink to='/sign-up'>Create account</RouterLink>
          </span>
        ) : (
          <RouterLink to='#' onClick={() => { setStep('email'); setError(''); setSuccess('') }}>
            ← Use a different email
          </RouterLink>
        )
      }>
      {error && <Alert style={{ marginBottom: 16 }} showIcon message={error} type='error' />}
      {success && step === 'code' && <Alert style={{ marginBottom: 16 }} showIcon message={success} type='success' />}

      {step === 'email' ? (
        <Form name='email-form' layout='vertical' initialValues={{ role: 'ROLE_ADMIN' }} onFinish={handleRequestCode} autoComplete='off'>
          <Form.Item name='role' label='Role' rules={[{ required: true }]}>
            <Select size='large' options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item name='email' label='Email' rules={[{ required: true, type: 'email', message: 'Enter a valid email.' }]}>
            <Input size='large' prefix={<MailOutlined style={{ color: '#9AA3B8' }} />} placeholder='you@organization.com' />
          </Form.Item>
          <Form.Item style={{ marginTop: 8 }}>
            <Button block size='large' type='primary' htmlType='submit' loading={loading}>
              Send Verification Code
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <Form name='code-form' layout='vertical' onFinish={handleVerifyCode} autoComplete='off'>
          <Form.Item name='code' label='Verification Code' rules={[{ required: true, message: 'Enter the code.' }]}>
            <Input size='large' prefix={<SafetyOutlined style={{ color: '#9AA3B8' }} />} placeholder='6-digit code' maxLength={6} />
          </Form.Item>
          <Form.Item style={{ marginTop: 8 }}>
            <Button block size='large' type='primary' htmlType='submit' loading={loading}>
              Verify & Sign In
            </Button>
          </Form.Item>
        </Form>
      )}
    </AuthLayout>
  )
}

export default Login
